/**
 * Web-Mercator helpers for projecting geographic points onto a Mapbox
 * Static Images API frame. Used to overlay an SVG (route line + markers)
 * that aligns with a static basemap rendered at a known center + zoom.
 */

import type { LngLat, MapViewport, PixelPoint } from "./types";

const TILE_SIZE = 512;
const MIN_ZOOM = 1;

function clamp(value: number, lo: number, hi: number): number {
  return Math.min(Math.max(value, lo), hi);
}

/** Normalized Mercator X in [0,1] for a longitude. */
function mercatorX(lng: number): number {
  return (lng + 180) / 360;
}

/** Normalized Mercator Y in [0,1] for a latitude (0 = north pole-ish). */
function mercatorY(lat: number): number {
  const s = Math.sin((lat * Math.PI) / 180);
  return 0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI);
}

export interface FitPointsOptions {
  /** Inset (px) kept clear on every edge. Default 24. */
  padding?: number;
  /** Never zoom in tighter than this. Default 14. */
  maxZoom?: number;
  /** Zoom used for a single point / degenerate bbox. Default 11. */
  defaultZoom?: number;
}

/**
 * Compute a static-map center + zoom that fits every point inside a
 * `width`×`height` viewport with padding. A single point (or identical
 * coords) falls back to `defaultZoom` centered on the point.
 */
export function fitPointsToView(
  points: LngLat[],
  width: number,
  height: number,
  options: FitPointsOptions = {},
): MapViewport {
  const padding = options.padding ?? 24;
  const maxZoom = options.maxZoom ?? 14;
  const defaultZoom = options.defaultZoom ?? 11;

  if (points.length === 0) {
    return { center: { lng: 0, lat: 0 }, zoom: defaultZoom };
  }

  let minLat = Number.POSITIVE_INFINITY;
  let maxLat = Number.NEGATIVE_INFINITY;
  let minLng = Number.POSITIVE_INFINITY;
  let maxLng = Number.NEGATIVE_INFINITY;
  for (const p of points) {
    if (p.lat < minLat) minLat = p.lat;
    if (p.lat > maxLat) maxLat = p.lat;
    if (p.lng < minLng) minLng = p.lng;
    if (p.lng > maxLng) maxLng = p.lng;
  }

  const center: LngLat = {
    lng: (minLng + maxLng) / 2,
    lat: (minLat + maxLat) / 2,
  };

  const latFraction = Math.abs(mercatorY(maxLat) - mercatorY(minLat));
  const lngFraction = Math.abs(mercatorX(maxLng) - mercatorX(minLng));
  if (latFraction === 0 && lngFraction === 0) {
    return { center, zoom: defaultZoom };
  }

  const usableW = Math.max(width - padding * 2, 1);
  const usableH = Math.max(height - padding * 2, 1);
  const zoomFor = (px: number, fraction: number): number =>
    fraction <= 0 ? maxZoom : Math.log2(px / TILE_SIZE / fraction);

  const zoom = clamp(
    Math.min(zoomFor(usableW, lngFraction), zoomFor(usableH, latFraction)),
    MIN_ZOOM,
    maxZoom,
  );
  return { center, zoom };
}

/**
 * Project a geographic point to a pixel within a `width`×`height` static
 * map rendered at `viewport`. (0,0) is the image's top-left; the viewport
 * center maps to (width/2, height/2).
 */
export function projectToPixel(
  point: LngLat,
  viewport: MapViewport,
  width: number,
  height: number,
): PixelPoint {
  const worldSize = TILE_SIZE * 2 ** viewport.zoom;
  const cx = mercatorX(viewport.center.lng) * worldSize;
  const cy = mercatorY(viewport.center.lat) * worldSize;
  return {
    x: mercatorX(point.lng) * worldSize - cx + width / 2,
    y: mercatorY(point.lat) * worldSize - cy + height / 2,
  };
}
